import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MONITORING_SERVICE, MonitoringService } from '@iese-chatbot/azure-insights';
import {
  AppState,
  selectActiveChat,
  selectChatDataLoading,
  selectGeneratingMessage,
  selectShowWaitMessage,
} from '@iese-chatbot/chat-data-access';
import { Chat, Citation, DataOrigin, Logger, MarkdownParsePipe, MathJaxDirective } from '@iese-chatbot/common-utils';
import { Store } from '@ngrx/store';
import { ButtonModule } from 'primeng/button';
import { MessagesModule } from 'primeng/messages';
import { SkeletonModule } from 'primeng/skeleton';
import { ChatMessageIconComponent } from '../../components/chat-message-icon/chat-message-icon.component';
import { CitationComponent } from '../../dialogs/citation/citation.component';

@Component({
  selector: 'icbc-main-chat-window',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MarkdownParsePipe,
    CitationComponent,
    MessagesModule,
    MathJaxDirective,
    ChatMessageIconComponent,
    SkeletonModule,
  ],
  templateUrl: './chat-messages.component.html',
})
export class ChatMessagesComponent implements AfterViewInit {
  @ViewChildren('messageRow', { read: ElementRef }) messageRows!: QueryList<ElementRef>;
  @ViewChild('chatMessagesContainer', { static: false }) chatMessagesContainer!: ElementRef<HTMLDivElement>;
  protected chat?: Chat;
  protected loading = true;
  protected generatingMessage = false;
  protected selectedCitation: Citation | undefined;
  protected showWaitMessage = false;
  protected readonly DataOrigin = DataOrigin;

  constructor(
    @Inject(MONITORING_SERVICE) private monitoringService: MonitoringService,
    private store: Store<AppState>,
  ) {
    this.store
      .select(selectChatDataLoading)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (loading) => {
          this.loading = loading;
        },
      });
    this.store
      .select(selectActiveChat)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (chat) => {
          const changed = this.chat?.id !== chat?.id;
          this.chat = chat;
          if (changed && chat) {
            setTimeout(() => this.scrollToLastUserMessage(), 50);
          }
        },
      });
    this.store
      .select(selectGeneratingMessage)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (generatingMessage) => {
          this.generatingMessage = generatingMessage;
        },
      });
    this.store
      .select(selectShowWaitMessage)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (showWaitMessage) => {
          this.showWaitMessage = showWaitMessage;
        },
      });
  }

  ngAfterViewInit(): void {
    this.scrollToLastUserMessage();
  }

  protected selectCitation(citation: Citation | undefined) {
    this.selectedCitation = citation;
    try {
      if (citation && citation.filepath) {
        this.monitoringService.trackEventWithProps('Citation_Click', {
          ChatId: this.chat?.id,
          ContentId: citation.filepath,
        });
      }
    } catch (error) {
      Logger.error(error);
    }
  }

  private scrollToLastUserMessage(): void {
    if (
      !this.chat ||
      !this.chat.messages ||
      this.chat.messages.length === 0 ||
      !this.messageRows ||
      this.messageRows.length === 0
    ) {
      return;
    }

    const scrollableContainerElement = this.chatMessagesContainer?.nativeElement;

    if (scrollableContainerElement) {
      scrollableContainerElement.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'start' });
    }

    let lastUserMessageIndex = -1;
    for (let i = this.chat.messages.length - 1; i >= 0; i--) {
      if (this.chat.messages[i].role === 'user') {
        lastUserMessageIndex = i;
        break;
      }
    }
    if (lastUserMessageIndex < 2) return;

    const messageElements = this.messageRows.toArray();
    if (messageElements[lastUserMessageIndex] && messageElements[lastUserMessageIndex].nativeElement) {
      messageElements[lastUserMessageIndex].nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }
}
